import { useMemo } from "react";

import { browserName, browserVersion, isAndroid } from "react-device-detect";

import supportedDashboardBrowsers from "../../../public/supported-dashboard-browsers.json";
import supportedFormsBrowsers from "../../../public/supported-forms-browsers.json";

import { isTest } from "@/constants/env";
import { SupportedBrowser } from "@/features/UnsupportedBrowser/all-supported-browsers";

function isInAppBrowser(browserName?: string): boolean {
  if (!browserName) {
    return false;
  }

  return [
    "Facebook",
    "Instagram",
    "WhatsApp",
    "Twitter",
    "LinkedIn",
    "Messenger",
    "TikTok",
    "WeChat",
    "Pinterest",
  ].includes(browserName);
}

type GetCurrentBrowserParams = {
  browserName?: string;
  browserVersion?: string;
  isAndroid?: boolean;
  userAgent?: string;
};

export function getCurrentBrowser({
  browserName,
  browserVersion,
  isAndroid,
}: GetCurrentBrowserParams): { browserKey: SupportedBrowser | "in-app"; browserVersion?: string } | null {
  if (isInAppBrowser(browserName)) {
    return { browserKey: "in-app", browserVersion: undefined };
  }

  if (browserName === "Chrome") {
    return { browserKey: isAndroid ? "and_chr" : "chrome", browserVersion };
  }

  if (browserName === "Edge") {
    return { browserKey: "edge", browserVersion };
  }

  if (browserName === "Firefox") {
    return { browserKey: isAndroid ? "and_ff" : "firefox", browserVersion };
  }

  if (browserName === "Safari") {
    return { browserKey: "safari", browserVersion };
  }

  if (browserName === "Samsung Browser") {
    return { browserKey: "samsung", browserVersion };
  }

  if (browserName === "Android Browser") {
    return { browserKey: "android", browserVersion };
  }

  if (browserName === "Mobile Safari") {
    return { browserKey: "ios_saf", browserVersion };
  }

  if (browserName === "Opera") {
    return { browserKey: "opera", browserVersion };
  }

  if (browserName === "Opera Mobile") {
    return { browserKey: "op_mob", browserVersion };
  }

  return null;
}

type IsBrowserSupportedParams = {
  browserName?: string;
  browserVersion?: string;
  supportedBrowsers: Record<string, number[]>;
  isAndroid?: boolean;
};

export const isBrowserSupported = ({
  browserName,
  browserVersion,
  supportedBrowsers,
  isAndroid,
}: IsBrowserSupportedParams): boolean => {
  if (isTest) {
    // Disable browser redirection for cypress tests
    return true;
  }

  if (!browserName || !browserVersion) {
    return false;
  }

  if (browserName === "GSA" || isInAppBrowser(browserName)) {
    // Let in-app browsers and search engines pass through
    return true;
  }

  const browser = getCurrentBrowser({ browserName, browserVersion, isAndroid });

  if (!browser) {
    return false;
  }

  const supportedVersions = supportedBrowsers[browser.browserKey];
  if (!supportedVersions || supportedVersions.length === 0) {
    return false;
  }

  // Extract major version number from browser version string
  const majorVersion = parseInt(browser.browserVersion?.split(".")[0] ?? "unknown", 10);
  if (isNaN(majorVersion)) {
    return false;
  }

  const minimalSupportedVersion = supportedVersions[0];

  return majorVersion >= minimalSupportedVersion;
};

export const useIsUnsupportedBrowserForDashboard = () => {
  return useMemo(() => {
    return !isBrowserSupported({
      browserName,
      browserVersion,
      supportedBrowsers: supportedDashboardBrowsers,
      isAndroid,
    });
  }, []);
};

export const useIsUnsupportedBrowserForForms = () => {
  return useMemo(() => {
    return !isBrowserSupported({
      browserName,
      browserVersion,
      supportedBrowsers: supportedFormsBrowsers,
      isAndroid,
    });
  }, []);
};
